/**
 * Workspace Draft Storage Utility
 * 
 * This utility provides a flexible interface for storing workspace draft data.
 * Currently uses localStorage, but designed to easily switch to base64/session storage
 * in the future without changing the API.
 */

import { z } from 'zod';

// Draft data schema - matches workspace creation schema but without images
export const workspaceDraftSchema = z.object({
  title: z.string(),
  content: z.string(),
  category: z.string(),
  categories: z.array(z.string()).default([]),
  address: z.string(),
  addressLine2: z.string().optional(),
  city: z.string(),
  postalCode: z.string(),
  phoneCountryCode: z.string(),
  phoneNumber: z.string(),
  latitude: z.string().optional().refine(
    (val) => !val || (/^-?\d+(\.\d+)?$/.test(val) && parseFloat(val) >= -90 && parseFloat(val) <= 90),
    { message: "Latitud debe ser un número válido entre -90 y 90" }
  ),
  longitude: z.string().optional().refine(
    (val) => !val || (/^-?\d+(\.\d+)?$/.test(val) && parseFloat(val) >= -180 && parseFloat(val) <= 180),
    { message: "Longitud debe ser un número válido entre -180 y 180" }
  ),
  countryCode: z.string().optional(),
  priceMin: z.number().optional(),
  priceMax: z.number().optional(),
  currency: z.string().length(3).default('EUR'),
  amenities: z.array(z.string()).default([]),
  rentalTypes: z.array(
    z.object({
      slug: z.string(),
      priceMin: z.number().optional(),
      priceMax: z.number().optional(),
      currency: z.string().length(3).default('EUR'),
    })
  ).default([]),
  slug: z.string().optional(),
  redirectEmail: z.string().email().optional().nullable(),
  // Future support for base64 images
  pendingImages: z.array(
    z.object({
      id: z.string(),
      fileName: z.string(),
      fileType: z.string(),
      fileSize: z.number(),
      base64: z.string(),
      altText: z.string().optional(),
    })
  ).optional(),
});

export type WorkspaceDraft = z.infer<typeof workspaceDraftSchema>;

const DRAFT_STORAGE_KEY = 'workspace_draft';
const DRAFT_TIMESTAMP_KEY = 'workspace_draft_timestamp';
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Storage adapter interface - allows switching storage backends
 */
interface StorageAdapter {
  save(data: WorkspaceDraft): void;
  load(): WorkspaceDraft | null;
  clear(): void;
  isExpired(): boolean;
}

/**
 * LocalStorage adapter (current implementation)
 */
class LocalStorageAdapter implements StorageAdapter {
  // Check if running in browser
  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  save(data: WorkspaceDraft): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(DRAFT_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to save workspace draft:', error);
      // Handle quota exceeded or other localStorage errors
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new Error('Storage quota exceeded. Please reduce the amount of data or complete signup first.');
      }
      throw error;
    }
  }

  load(): WorkspaceDraft | null {
    if (!this.isBrowser()) return null;
    
    try {
      const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!stored) return null;

      // Check if expired
      if (this.isExpired()) {
        this.clear();
        return null;
      }

      const parsed = JSON.parse(stored);
      const validated = workspaceDraftSchema.safeParse(parsed);
      
      return validated.success ? validated.data : null;
    } catch (error) {
      console.error('Failed to load workspace draft:', error);
      return null;
    }
  }

  clear(): void {
    if (!this.isBrowser()) return;
    
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Failed to clear workspace draft:', error);
    }
  }

  isExpired(): boolean {
    if (!this.isBrowser()) return true;
    
    try {
      const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
      if (!timestamp) return true;

      const age = Date.now() - parseInt(timestamp, 10);
      return age > DRAFT_MAX_AGE_MS;
    } catch {
      return true;
    }
  }
}

/**
 * Future: Base64/Cookie adapter for more secure storage
 * Uncomment and implement when needed
 */
/*
class Base64CookieAdapter implements StorageAdapter {
  save(data: WorkspaceDraft): void {
    // TODO: Implement cookie-based storage with base64 encoding
    // Will require server-side support
  }

  load(): WorkspaceDraft | null {
    // TODO: Implement cookie-based loading
    return null;
  }

  clear(): void {
    // TODO: Implement cookie clearing
  }

  isExpired(): boolean {
    // TODO: Check cookie expiration
    return false;
  }
}
*/

// Use localStorage adapter by default
// To switch to cookie adapter in the future, just change this line
const storageAdapter: StorageAdapter = new LocalStorageAdapter();

/**
 * Public API - these functions abstract the storage mechanism
 */

/**
 * Save workspace draft data
 */
export function saveWorkspaceDraft(data: Partial<WorkspaceDraft>): void {
  // Validate and fill in defaults
  const validated = workspaceDraftSchema.parse(data);
  storageAdapter.save(validated);
}

/**
 * Load workspace draft data
 * Returns null if no draft exists or if expired
 */
export function loadWorkspaceDraft(): WorkspaceDraft | null {
  return storageAdapter.load();
}

/**
 * Clear workspace draft data
 */
export function clearWorkspaceDraft(): void {
  storageAdapter.clear();
}

/**
 * Check if a draft exists and is not expired
 */
export function hasDraft(): boolean {
  return loadWorkspaceDraft() !== null;
}

/**
 * Check if draft is expired
 */
export function isDraftExpired(): boolean {
  return storageAdapter.isExpired();
}

/**
 * Get draft age in milliseconds
 */
export function getDraftAge(): number | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null;
  }
  
  try {
    const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
    if (!timestamp) return null;
    return Date.now() - parseInt(timestamp, 10);
  } catch {
    return null;
  }
}

/**
 * Format draft age as human-readable string
 */
export function formatDraftAge(): string | null {
  const age = getDraftAge();
  if (!age) return null;

  const hours = Math.floor(age / (60 * 60 * 1000));
  const minutes = Math.floor((age % (60 * 60 * 1000)) / (60 * 1000));

  if (hours > 0) {
    return `hace ${hours} hora${hours !== 1 ? 's' : ''}`;
  }
  return `hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
}
